import { Button, Form, Input, Modal, Select } from 'antd';
import { FC, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalStateContext, useUIDispatchContext } from 'providers/UI';
import { useOrganizationsStateContext } from 'providers/Organizations';
import { PlusOutlined } from '@ant-design/icons';
import { createDoctor, updateUser } from 'services/api/users';

import { showErrorMessage } from 'utils/showErrorMessage';
import { OrganizationAlert } from 'components/shared';

interface IProps {
  onSaved?: () => void;
}

const AddDoctor: FC<IProps> = ({ onSaved }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { toggleModal } = useUIDispatchContext();
  const { doctorDetails } = useModalStateContext();
  const { selected, results } = useOrganizationsStateContext();

  const doctorId = useMemo(
    () => doctorDetails.params?.id,
    [doctorDetails.params?.id]
  );

  const closeModal = () => {
    form.resetFields();
    toggleModal('doctorDetails', false);
  };

  const [isSaving, setIsSaving] = useState(false);

  const onFinish = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (values: Record<string, any> & { organization_id?: string }) => {
      const organizationHeader =
        selected === 'all' ? values.organization_id : undefined;

      delete values.organization_id;

      try {
        setIsSaving(true);

        if (doctorId) {
          await updateUser(doctorId, values);
        } else {
          await createDoctor(values, organizationHeader);
        }

        onSaved?.();
        closeModal();
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setIsSaving(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doctorId, selected, onSaved]
  );

  return (
    <>
      <OrganizationAlert>
        <Button
          type="primary"
          onClick={() => toggleModal('doctorDetails', true)}
        >
          <PlusOutlined />
          {t('doctors.add')}
        </Button>
      </OrganizationAlert>
      <Modal
        title={t(doctorId ? 'doctors.edit' : 'doctors.add')}
        onCancel={closeModal}
        open={doctorDetails.visible}
        width={600}
        onOk={form.submit}
        confirmLoading={isSaving}
        destroyOnClose
      >
        <Form form={form} onFinish={onFinish} layout="vertical" size="large">
          <Form.Item
            name="firstName"
            label={t('doctors.first_name')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="lastName"
            label={t('doctors.last_name')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label={t('doctors.email')}
            rules={[{ required: true }, { type: 'email' }]}
          >
            <Input />
          </Form.Item>
          {selected === 'all' && (
            <Form.Item
              name="organization_id"
              label="Organization"
              rules={[{ required: true }]}
            >
              <Select options={results.filter(item => item.value !== 'all')} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default memo(AddDoctor);
